/**
 * Money is integer micros of the account currency (bigint in code, string in JSON).
 * Never use floats for money; convert to a number only for display.
 */

export const MICROS = 1_000_000n;

export interface MoneyJson {
  micros: string;
  currency: string;
}

export function money(micros: bigint, currency: string): MoneyJson {
  return { micros: micros.toString(), currency };
}

export function toMicros(m: MoneyJson | string | bigint | null | undefined): bigint {
  if (m === null || m === undefined) return 0n;
  if (typeof m === "bigint") return m;
  return BigInt(typeof m === "string" ? m : m.micros);
}

/** Decimal amount (e.g. from a form, "12.50") -> micros. Rounds half away from zero at 6 decimals. */
export function parseAmount(text: string): bigint | null {
  const s = text.trim().replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const neg = s.startsWith("-");
  const [whole = "0", frac = ""] = s.replace("-", "").split(".");
  const padded = `${frac}0000000`.slice(0, 7);
  let micros = BigInt(whole) * MICROS + BigInt(padded.slice(0, 6));
  if (Number(padded[6]) >= 5) micros += 1n;
  return neg ? -micros : micros;
}

/** Cents (v1 storage) -> micros. */
export function centsToMicros(cents: number | bigint | null | undefined): bigint {
  if (cents === null || cents === undefined) return 0n;
  return BigInt(cents) * 10_000n;
}

// building a formatter is slow, and rules format thousands of amounts: one per currency and precision
const formatters = new Map<string, Intl.NumberFormat>();
function formatter(currency: string, decimals: number): Intl.NumberFormat {
  const key = `${currency}:${decimals}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formatters.set(key, f);
  }
  return f;
}

/** Display: "$1,234" (2 decimals under 100), leading "−" for negatives. */
export function formatMoney(
  micros: bigint | string | null | undefined,
  currency = "USD",
  decimals?: number,
): string {
  if (micros === null || micros === undefined) return "—";
  const v = Number(BigInt(micros)) / 1e6;
  const d = decimals ?? (Math.abs(v) < 100 ? 2 : 0);
  const s = formatter(currency, d).format(Math.abs(v));
  return v < 0 ? `−${s}` : s;
}

/** a / b as a JS number for ratios (POAS, ROAS); null when b is 0. */
export function ratio(a: bigint, b: bigint): number | null {
  if (b === 0n) return null;
  return Number(a) / Number(b);
}
